import type { ReportStrategyPolicyVersion } from "../../../types/reports";
import type { ReportStrategyPineFields, StrategyActivePineSummary } from "../../../types/reportStrategyPine";
import { formatTradeDateTimeForDisplay } from "../../../utils/displayDateTime";
import { strategyHasTradingViewPinescriptCreated } from "../../../utils/reportStrategyPineUi";

export const VERSION_LABELS_STORAGE_KEY = "strategy_policy_version_labels_v1";

export type PolicyRuleKey = string;

export function safeDate(s: unknown): Date | null {
  if (typeof s !== "string" || !s.trim()) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtUtcLike(s: unknown): string {
  const d = safeDate(s);
  if (!d) return "—";
  return d.toISOString().replace("T", " ").slice(0, 19) + "Z";
}

/** Created/updated labels in the user's Display Preferences timezone (or device zone). */
export function formatStrategyTimestamp(s: unknown, timeZone?: string): string | null {
  if (typeof s !== "string" || !s.trim()) return null;
  const formatted = formatTradeDateTimeForDisplay(s, timeZone);
  return formatted === "—" ? null : formatted;
}

export function versionLabel(v: ReportStrategyPolicyVersion): string {
  if (typeof v.version_number === "number" && Number.isFinite(v.version_number)) {
    return `v${v.version_number}`;
  }
  return v.created_at ? fmtUtcLike(v.created_at) : "version";
}

/** Max length for `PATCH .../policy-versions/{id}` `{ title }` (BE contract). */
export const POLICY_VERSION_TITLE_MAX_LENGTH = 180;

/**
 * Durable display name for a policy / combined policy version.
 * Custom title → source simulation title → Policy vN.
 */
export function policyVersionDisplayTitle(v: ReportStrategyPolicyVersion): string {
  const custom = typeof v.title === "string" ? v.title.trim() : "";
  if (custom) return custom;
  const fromSim =
    typeof v.source_backtest_title === "string" ? v.source_backtest_title.trim() : "";
  if (fromSim) return fromSim;
  if (typeof v.version_number === "number" && Number.isFinite(v.version_number)) {
    return `Policy v${v.version_number}`;
  }
  return versionLabel(v);
}

/** Policy version ids used to generate the active TradingView Pinescript, when one exists. */
export function getActiveComposedPolicyVersionIds(
  activePine?: StrategyActivePineSummary | null,
  strategy?: Pick<ReportStrategyPineFields, "has_composed_pine" | "active_pine"> | null
): string[] {
  const context: ReportStrategyPineFields = {
    ...(strategy ?? {}),
    active_pine: activePine ?? strategy?.active_pine,
  };
  if (!strategyHasTradingViewPinescriptCreated(context)) return [];

  const raw = context.active_pine?.last_compose_policy_version_ids;
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === "string" && id.length > 0);
}

export function isActiveComposedPolicyVersion(
  policyVersionId: string,
  activePine?: StrategyActivePineSummary | null,
  strategy?: Pick<ReportStrategyPineFields, "has_composed_pine" | "active_pine"> | null
): boolean {
  return getActiveComposedPolicyVersionIds(activePine, strategy).includes(policyVersionId);
}

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

/** Collapse a chain of single-child groups with the same op (common simulator save shape). */
export function unwrapRedundantPolicyGroup(node: unknown): unknown {
  if (!isPlainObject(node) || node.kind !== "group") return node;
  let current: Record<string, unknown> = node;
  while (true) {
    const op = typeof current.op === "string" ? current.op : "and";
    const children = Array.isArray(current.children) ? current.children : [];
    if (children.length !== 1) break;
    const only = children[0];
    if (!isPlainObject(only) || only.kind !== "group") break;
    const childOp = typeof only.op === "string" ? only.op : "and";
    if (childOp !== op) break;
    current = only;
  }
  return current;
}

export function stableJson(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export function extractPolicyRuleKeys(policyTree: unknown): PolicyRuleKey[] {
  if (!isPlainObject(policyTree)) return [];
  const root = (policyTree as { root?: unknown }).root;
  const out: string[] = [];
  const visit = (n: unknown) => {
    if (!n || typeof n !== "object") return;
    const node = n as Record<string, unknown>;
    if (node.kind === "rule") {
      const path = typeof node.path === "string" ? node.path : "";
      const op = typeof node.op === "string" ? node.op : "";
      const value = "value" in node ? stableJson(node.value) : "";
      if (path && op) out.push(`${path} ${op} ${value}`);
      return;
    }
    if (node.kind === "group" && Array.isArray(node.children)) {
      for (const c of node.children) visit(c);
    }
  };
  visit(root);
  return out;
}

export function diffRuleKeys(
  prev: PolicyRuleKey[],
  next: PolicyRuleKey[]
): { added: PolicyRuleKey[]; removed: PolicyRuleKey[] } {
  const a = new Set(prev);
  const b = new Set(next);
  const added: string[] = [];
  const removed: string[] = [];
  for (const k of Array.from(b)) if (!a.has(k)) added.push(k);
  for (const k of Array.from(a)) if (!b.has(k)) removed.push(k);
  added.sort();
  removed.sort();
  return { added, removed };
}

export type StrategyDetailLocationState = {
  strategyDisplayName?: string;
  /** Opened from the Workspace hub — breadcrumb parent is Workspace, not Strategies. */
  fromWorkspace?: true;
};

export type PolicyVersionSourceDisplay = {
  text: string;
  title?: string;
};

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((x): x is string => typeof x === "string" && x.trim().length > 0);
}

function asNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((x) => (typeof x === "number" ? x : typeof x === "string" ? Number(x) : NaN))
    .filter((n) => Number.isFinite(n));
}

export function extractCombinedSourceRefsFromPolicyTree(tree: unknown): {
  versionIds: string[];
  versionNumbers: number[];
} {
  const versionIds: string[] = [];
  const versionNumbers: number[] = [];
  if (!isPlainObject(tree)) return { versionIds, versionNumbers };

  versionIds.push(
    ...asStringArray(tree.source_policy_version_ids ?? tree.combined_from_policy_version_ids),
  );
  versionNumbers.push(
    ...asNumberArray(
      tree.source_policy_version_numbers ?? tree.combined_from_policy_version_numbers,
    ),
  );

  const segments = Array.isArray(tree.segments) ? tree.segments : [];
  for (const segment of segments) {
    if (!isPlainObject(segment)) continue;
    const id =
      (typeof segment.source_policy_version_id === "string" &&
        segment.source_policy_version_id.trim()) ||
      (typeof segment.policy_version_id === "string" && segment.policy_version_id.trim()) ||
      "";
    if (id) versionIds.push(id);

    const number =
      typeof segment.source_policy_version_number === "number"
        ? segment.source_policy_version_number
        : typeof segment.source_version_number === "number"
          ? segment.source_version_number
          : typeof segment.version_number === "number"
            ? segment.version_number
            : NaN;
    if (Number.isFinite(number)) versionNumbers.push(number);

    const sourceTitle =
      typeof segment.source_title === "string" ? segment.source_title.trim() : "";
    const versionMatch = /^v(\d+)$/i.exec(sourceTitle);
    if (versionMatch) versionNumbers.push(Number(versionMatch[1]));
  }

  return { versionIds, versionNumbers };
}

function sortVersionLabels(labels: string[]): string[] {
  return [...labels].sort((a, b) => {
    const na = /^v(\d+)$/i.exec(a)?.[1];
    const nb = /^v(\d+)$/i.exec(b)?.[1];
    if (na && nb) return Number(na) - Number(nb);
    return a.localeCompare(b);
  });
}

export function resolveCombinedSourceVersions(
  version: ReportStrategyPolicyVersion,
  allVersions: ReportStrategyPolicyVersion[],
): ReportStrategyPolicyVersion[] | null {
  const versionsById = new Map(allVersions.map((v) => [v.id, v]));
  const versionsByNumber = new Map<number, ReportStrategyPolicyVersion>();
  for (const v of allVersions) {
    if (typeof v.version_number === "number" && Number.isFinite(v.version_number)) {
      versionsByNumber.set(v.version_number, v);
    }
  }

  const treeRefs = version.policy_tree
    ? extractCombinedSourceRefsFromPolicyTree(version.policy_tree)
    : { versionIds: [], versionNumbers: [] };

  const versionIds = [...asStringArray(version.source_policy_version_ids), ...treeRefs.versionIds];
  const versionNumbers = [
    ...asNumberArray(version.source_policy_version_numbers),
    ...treeRefs.versionNumbers,
  ];

  const resolved: ReportStrategyPolicyVersion[] = [];
  const seenIds = new Set<string>();
  const seenNumbers = new Set<number>();

  for (const id of versionIds) {
    if (seenIds.has(id)) continue;
    seenIds.add(id);
    const match = versionsById.get(id);
    if (match) resolved.push(match);
  }

  for (const number of versionNumbers) {
    if (seenNumbers.has(number)) continue;
    seenNumbers.add(number);
    const match = versionsByNumber.get(number);
    if (match && !seenIds.has(match.id)) {
      seenIds.add(match.id);
      resolved.push(match);
    }
  }

  if (resolved.length < 2) return null;

  resolved.sort((a, b) => {
    const na = a.version_number;
    const nb = b.version_number;
    if (typeof na === "number" && typeof nb === "number") return na - nb;
    return versionLabel(a).localeCompare(versionLabel(b));
  });

  return resolved;
}

function resolveCombinedSourceLabels(
  version: ReportStrategyPolicyVersion,
  allVersions: ReportStrategyPolicyVersion[],
): string[] | null {
  const sources = resolveCombinedSourceVersions(version, allVersions);
  if (!sources?.length) return null;
  return sortVersionLabels(sources.map((v) => versionLabel(v)));
}

function resolveDirectSimulationLabel(version: ReportStrategyPolicyVersion): string | null {
  const title = version.source_backtest_title?.trim();
  if (title) return title;
  const backtestId = version.source_backtest_id?.trim();
  if (backtestId) return `sim ${backtestId.slice(0, 8)}…`;
  return null;
}

function resolveSimulationSourceDisplay(
  version: ReportStrategyPolicyVersion,
): PolicyVersionSourceDisplay | null {
  const direct = resolveDirectSimulationLabel(version);
  if (!direct) return null;
  const backtestId = version.source_backtest_id?.trim();
  return {
    text: direct,
    title: version.source_backtest_title?.trim() || (backtestId ? `simulation ${backtestId}` : direct),
  };
}

/** Simulation label column — direct backtest title or merged labels from combined sources. */
export function resolvePolicyVersionSimulationLabel(
  version: ReportStrategyPolicyVersion,
  allVersions: ReportStrategyPolicyVersion[],
): PolicyVersionSourceDisplay | null {
  const combinedSources = resolveCombinedSourceVersions(version, allVersions);
  if (combinedSources?.length) {
    const labels = combinedSources
      .map((source) => resolveDirectSimulationLabel(source))
      .filter((label): label is string => Boolean(label));
    if (labels.length === 0) return null;
    const text = labels.join(" & ");
    return { text, title: text };
  }

  return resolveSimulationSourceDisplay(version);
}

/** Source column label — combined policy provenance (v17 & v18) or simulation/report origin. */
export function resolvePolicyVersionSourceDisplay(
  version: ReportStrategyPolicyVersion,
  allVersions: ReportStrategyPolicyVersion[],
): PolicyVersionSourceDisplay | null {
  const combinedLabels = resolveCombinedSourceLabels(version, allVersions);
  if (combinedLabels?.length) {
    const text = combinedLabels.join(" & ");
    return {
      text,
      title: `Combined from ${combinedLabels.join(", ")}`,
    };
  }

  return resolveSimulationSourceDisplay(version);
}

/** True when a saved simulation already has a policy version row sourced from it. */
export function isBacktestSavedAsPolicyVersion(
  backtestId: string | null | undefined,
  policyVersions: ReportStrategyPolicyVersion[] | null | undefined,
): boolean {
  const id = backtestId?.trim();
  if (!id || !Array.isArray(policyVersions) || policyVersions.length === 0) return false;
  return policyVersions.some((version) => version.source_backtest_id?.trim() === id);
}
